import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  LinearProgress,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  TextField,
  Divider,
  Tooltip,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import CalculateIcon from '@mui/icons-material/Calculate';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Description, PictureAsPdf, Refresh, SaveAlt } from '@mui/icons-material';
import { useObtenerFiltrosVentas } from '@/shared/actions/erp';
import { useCalcularComisiones, useFetchReporteAsesor, useExportarComisiones, useFetchTaskStatus, useFetchReporteCoordinador, useRegistrarChargebacksHistoricos } from '@/shared/actions/erp/comisiones/comisiones.actions';
import type { FiltrosComisionAsesor, ComisionAsesorRow } from '@/lib/commissions/types';
import { usePeriodo } from '../context/PeriodoContext';
import PeriodoIndicator from './PeriodoIndicator';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';

function Row({ row }: { row: ComisionAsesorRow }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          '&:hover': { bgcolor: 'action.hover' },
          transition: 'background-color 0.2s'
        }}
      >
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          >
            <KeyboardArrowDownIcon />
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>{row.asesor_nombre}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 500 }}>{row.total_planes}</TableCell>
        <TableCell align="right">
          <Box
            component="span"
            sx={{
              bgcolor: 'info.light',
              color: 'info.dark',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontWeight: 600
            }}
          >
            {(Number(row.margen) * 100).toFixed(0)}%
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 500 }}>${Number(row.total_vendido).toFixed(2)}</TableCell>
        <TableCell align="right">${Number(row.bono).toFixed(2)}</TableCell>
        <TableCell align="right">${Number(row.alimentacion).toFixed(2)}</TableCell>
        <TableCell align="right">${Number(row.movilizacion).toFixed(2)}</TableCell>
        <TableCell align="right">
          <Box
            component="span"
            sx={{
              bgcolor: row.chargeback_count > 0 ? 'error.light' : 'transparent',
              color: row.chargeback_count > 0 ? 'error.dark' : 'inherit',
              px: row.chargeback_count > 0 ? 1 : 0,
              py: row.chargeback_count > 0 ? 0.5 : 0,
              borderRadius: 1,
              fontWeight: row.chargeback_count > 0 ? 600 : 400
            }}
          >
            {row.chargeback_count}
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
          -${Number(row.chargeback_total || 0).toFixed(2)}
        </TableCell>
        <TableCell align="right">
          <Box
            component="span"
            sx={{
              bgcolor: 'success.light',
              color: 'black',
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              fontWeight: 700,
              fontSize: '0.9rem'
            }}
          >
            ${Number(row.total_comision).toFixed(2)}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Detalle por Plan
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.light', color: 'primary.contrastText' }}>Plan</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'primary.light', color: 'primary.contrastText' }}>Cantidad</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'primary.light', color: 'primary.contrastText' }}>Precio Unit.</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'primary.light', color: 'primary.contrastText' }}>Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.detalles?.map((detalle, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          '&:hover': { bgcolor: 'action.hover' },
                          '&:last-child td': { borderBottom: 0 }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{detalle.plan_code}</TableCell>
                        <TableCell align="right">{detalle.cantidad}</TableCell>
                        <TableCell align="right">${Number(detalle.precio_unitario).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>${Number(detalle.subtotal).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Desglose del Cálculo de Comisiones */}
              {row.desglose_calculo && (
                <>
                  <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 600, color: 'warning.main', mt: 3 }}>
                    Desglose del Cálculo de Comisiones
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {/* Tier aplicado */}
                    {row.desglose_calculo.tier_aplicado && (
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.50' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.dark', mb: 1 }}>
                          📊 Tier de Comisión Aplicado
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Rango</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.desglose_calculo.tier_aplicado.desde} - {row.desglose_calculo.tier_aplicado.hasta} planes
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Factor</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.dark' }}>
                              {row.desglose_calculo.tier_aplicado.factor_percent.toFixed(0)}% (×{row.desglose_calculo.tier_aplicado.factor.toFixed(2)})
                            </Typography>
                          </Box>
                          <Box sx={{ ml: 'auto', bgcolor: 'info.main', px: 2, py: 1, borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: 'white' }}>Comisión por Tier</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'white' }}>
                              ${(row.total_vendido * row.margen).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    )}

                    {/* Bonos por escalón */}
                    {row.desglose_calculo.bonos_aplicados && row.desglose_calculo.bonos_aplicados.length > 0 && (
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark', mb: 1 }}>
                          💰 Bonos por Escalón Cumplidos{row.desglose_calculo.ciudad ? ` - ${row.desglose_calculo.ciudad}` : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {row.desglose_calculo.bonos_aplicados.map((bono, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                border: '1.5px solid',
                                borderColor: 'success.main',
                                bgcolor: 'transparent',
                                px: 2,
                                py: 1,
                                borderRadius: 1
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">Umbral: {bono.umbral} planes</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.dark' }}>
                                +${bono.monto.toFixed(2)}
                              </Typography>
                            </Box>
                          ))}
                          <Box sx={{ bgcolor: 'success.main', px: 2, py: 1, borderRadius: 1, ml: 'auto' }}>
                            <Typography variant="caption" sx={{ color: 'white' }}>Total Bonos</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'white' }}>
                              ${row.desglose_calculo.totales.bono_escalon.toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    )}

                    {/* Vales (Alimentación y Movilización) */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {row.desglose_calculo.vale_alimentacion && (
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.50', flex: 1, minWidth: 200 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.dark', mb: 1 }}>
                            🍽️ Vale de Alimentación
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Umbral: {row.desglose_calculo.vale_alimentacion.umbral} planes
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                            ${row.desglose_calculo.vale_alimentacion.monto.toFixed(2)}
                          </Typography>
                        </Paper>
                      )}
                      {row.desglose_calculo.vale_movilizacion && (
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'secondary.50', flex: 1, minWidth: 200 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.dark', mb: 1 }}>
                            🚗 Vale de Movilización
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Umbral: {row.desglose_calculo.vale_movilizacion.umbral} planes
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'secondary.dark' }}>
                            ${row.desglose_calculo.vale_movilizacion.monto.toFixed(2)}
                          </Typography>
                        </Paper>
                      )}
                    </Box>

                    {/* Ciudad (si está disponible) */}
                    {row.desglose_calculo.ciudad && (
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.100' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                          📍 Ciudad
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.desglose_calculo.ciudad}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </>
              )}

              {/* Detalle de Contratos Cancelados (Chargebacks) */}
              {row.contratos_cancelados && row.contratos_cancelados.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 600, color: 'error.main', mt: 3 }}>
                    Contratos Cancelados (Chargebacks)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'error.light', color: 'error.contrastText' }}>Cliente</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'error.light', color: 'error.contrastText' }}>Plan</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'error.light', color: 'error.contrastText' }}>Valor Mensual</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'error.light', color: 'error.contrastText' }}>Fecha Instalación</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'error.light', color: 'error.contrastText' }}>Fecha Final</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'error.light', color: 'error.contrastText' }}>Tiempo</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {row.contratos_cancelados.map((contrato, idx) => (
                          <TableRow
                            key={idx}
                            sx={{
                              '&:hover': { bgcolor: 'action.hover' },
                              '&:last-child td': { borderBottom: 0 }
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>{contrato.nombre_cliente}</TableCell>
                            <TableCell>{contrato.plan}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>
                              ${Number(contrato.valor_mensual).toFixed(2)}
                            </TableCell>
                            <TableCell>{contrato.fecha_instalacion}</TableCell>
                            <TableCell>{contrato.fecha_final}</TableCell>
                            <TableCell>{contrato.tiempo_transcurrido}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

interface TabAsesorProps {
  onOpenParametrizacion: () => void;
}

export default function TabAsesor({ onOpenParametrizacion }: TabAsesorProps) {
  const {
    fechaInicio: periodoInicio,
    fechaFin: periodoFin,
    fechaInicioCallCenter: periodoInicioCallCenter,
    fechaFinCallCenter: periodoFinCallCenter,
  } = usePeriodo();

  const [filtros, setFiltros] = useState<FiltrosComisionAsesor>({
    fecha_inicio: periodoInicio,
    fecha_fin: periodoFin,
    canales: [],
    period_type: 'HOME',
  });
  const [canalSearch, setCanalSearch] = useState('');

  // Usar estados compartidos del contexto
  const {
    currentRunId,
    setCurrentRunId,
    taskId,
    setTaskId,
    isCalculating,
    setIsCalculating,
    canalesCalculados,
    setCanalesCalculados,
  } = usePeriodo();

  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState<'xlsx' | 'pdf' | ''>('');
  const [exportProgress, setExportProgress] = useState(0);
  const [expandedSupervisor, setExpandedSupervisor] = useState<number | null>(null);

  // Estados para el diálogo de selección de exportación
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [selectedExportFormat, setSelectedExportFormat] = useState<'xlsx' | 'pdf'>('xlsx');
  const [selectedTables, setSelectedTables] = useState({
    asesores: true,
    supervisores: false
  });

  // Sincronizar filtros con el contexto de período HOME
  useEffect(() => {
    if (filtros.period_type === 'HOME') {
      setFiltros(prev => ({
        ...prev,
        fecha_inicio: periodoInicio,
        fecha_fin: periodoFin,
      }));
    }
  }, [periodoInicio, periodoFin, filtros.period_type]);

  // Sincronizar filtros con el contexto de período CALL CENTER
  useEffect(() => {
    if (filtros.period_type === 'CALL_CENTER') {
      setFiltros(prev => ({
        ...prev,
        fecha_inicio: periodoInicioCallCenter,
        fecha_fin: periodoFinCallCenter,
      }));
    }
  }, [periodoInicioCallCenter, periodoFinCallCenter, filtros.period_type]);

  // Detectar cuando se selecciona "Vendedor Call Center" y cambiar automáticamente period_type y fechas
  useEffect(() => {
    const canalesSeleccionados = filtros.canales ?? [];
    const tieneCallCenter = canalesSeleccionados.some(canal =>
      canal.toLowerCase().includes('vendedor call center')
    );

    // Si se selecciona "Vendedor Call Center", cambiar a CALL_CENTER
    // Si no, mantener HOME
    const nuevoPeriodType = tieneCallCenter ? 'CALL_CENTER' : 'HOME';

    if (filtros.period_type !== nuevoPeriodType) {
      // Cambiar el tipo de período y las fechas correspondientes
      const nuevasFechas = nuevoPeriodType === 'CALL_CENTER'
        ? { fecha_inicio: periodoInicioCallCenter, fecha_fin: periodoFinCallCenter }
        : { fecha_inicio: periodoInicio, fecha_fin: periodoFin };

      setFiltros(prev => ({
        ...prev,
        period_type: nuevoPeriodType,
        ...nuevasFechas,
      }));
    }
  }, [filtros.canales, filtros.period_type, periodoInicio, periodoFin, periodoInicioCallCenter, periodoFinCallCenter]);

  const { data: filtrosData, isLoading: isLoadingFiltros, refetch: refetchFiltros } = useObtenerFiltrosVentas();
  const calcularMutation = useCalcularComisiones({ disableGlobalLoading: true });
  const { data: resultados, isLoading: isLoadingResultados } = useFetchReporteAsesor(
    currentRunId,
    !!currentRunId
  );
  const { data: coordinadorData } = useFetchReporteCoordinador(
    currentRunId,
    !!currentRunId
  );

  // Polling para el estado de la tarea de Celery
  const { data: taskStatus } = useFetchTaskStatus(taskId, isCalculating && !!taskId);

  // Actualizar estado cuando cambia el taskStatus
  useEffect(() => {
    if (!taskStatus) return;

    const state = taskStatus.state;

    // Si la tarea terminó exitosamente
    if (state === 'SUCCESS' && taskStatus.run_id) {
      setCurrentRunId(taskStatus.run_id);
      setTimeout(() => {
        setIsCalculating(false);
        setTaskId(null);
      }, 500);
    }

    // Si la tarea falló
    if (state === 'FAILURE') {
      setIsCalculating(false);
      setTaskId(null);
    }
  }, [taskStatus, setCurrentRunId, setIsCalculating, setTaskId]);

  const exportarMutation = useExportarComisiones();
  const registrarCBMutation = useRegistrarChargebacksHistoricos();

  // Estados para modal de confirmación de registro de chargebacks
  const [openRegistrarCBDialog, setOpenRegistrarCBDialog] = useState(false);

  // Obtener lista de canales disponibles desde el endpoint (array de strings)
  // Filtrar valores nulos o vacíos
  const canalesDisponibles = useMemo(() => {
    if (!filtrosData?.data) return [];
    try {
      const canales = (filtrosData.data as any).canales || [];
      return canales.filter((canal: any) => canal != null && canal !== '');
    } catch (error) {
      return [];
    }
  }, [filtrosData]);

  // Filtrar canales por búsqueda
  const canalesFiltrados = useMemo(() => {
    if (!canalSearch) return canalesDisponibles;
    return canalesDisponibles.filter((canal: string) =>
      canal.toLowerCase().includes(canalSearch.toLowerCase())
    );
  }, [canalesDisponibles, canalSearch]);

  const selectedCanales = filtros.canales ?? [];


  const handleClearDates = () => {
    setFiltros((prev) => ({
      ...prev,
      fecha_inicio: periodoInicio,
      fecha_fin: periodoFin,
    }));
  };

  const handleChangeCanales = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    if (value.includes('SELECCIONAR_TODOS')) {
      setFiltros((prev) => ({
        ...prev,
        canales: selectedCanales.length === canalesDisponibles.length ? [] : canalesDisponibles,
      }));
    } else {
      setFiltros((prev) => ({
        ...prev,
        canales: value,
      }));
    }
  };

  const handleRefreshFiltros = async () => {
    await refetchFiltros();
  };

  const handleCalcular = async () => {
    // Si ya hay un cálculo en progreso, no hacer nada
    if (isCalculating) return;

    setIsCalculating(true);

    // Guardar los canales usados en este cálculo en el contexto compartido
    setCanalesCalculados(filtros.canales || []);

    try {
      // Llamar al backend que ahora devuelve task_id
      const result = await calcularMutation.mutateAsync(filtros);

      // result ahora contiene { task_id, status, message }
      if (result && result.task_id) {
        setTaskId(result.task_id);
        // El polling se encargará del resto a través del useEffect
      } else {
        // Fallback: si todavía viene el formato antiguo
        if (result && result.run && result.run.id) {
          setCurrentRunId(result.run.id);
          setTimeout(() => {
            setIsCalculating(false);
          }, 500);
        }
      }
    } catch (error) {
      setIsCalculating(false);
      setTaskId(null);
    }
  };

  const handleOpenExportDialog = () => {
    setExportDialogOpen(true);
  };

  const handleConfirmExport = async () => {
    setExportDialogOpen(false);

    // Validar que al menos una tabla esté seleccionada
    if (!selectedTables.asesores && !selectedTables.supervisores) {
      return;
    }

    // Para Excel y PDF, usar la función de exportación con parámetros de tablas
    await handleExportarConTablas(selectedExportFormat, selectedTables);
  };

  /**
   * Exporta el reporte con las tablas seleccionadas
   *
   * ============================================================================
   * NOTA IMPORTANTE PARA BACKEND - REQUIERE ACTUALIZACIÓN
   * ============================================================================
   *
   * PROBLEMA ACTUAL:
   * - El backend actualmente solo exporta datos de Asesores
   * - Los parámetros incluir_asesores e incluir_supervisores se envían pero NO se procesan
   * - Cuando se selecciona "Supervisores", el archivo descargado NO incluye esos datos
   *
   * SOLUCIÓN REQUERIDA EN BACKEND:
   *
   * El endpoint /api/v1/comisiones/runs/{run_id}/export/ debe:
   *
   * 1. RECIBIR Y LEER los parámetros query:
   *    - incluir_asesores: string ('true' o 'false')
   *    - incluir_supervisores: string ('true' o 'false')
   *
   * 2. CONSULTAR datos según selección:
   *    - Si incluir_asesores='true': Consultar CommissionAsesor para el run_id
   *    - Si incluir_supervisores='true': Consultar ComisionSupervisor para el run_id
   *
   * 3. GENERAR ARCHIVO con las tablas seleccionadas:
   *
   *    A) PARA EXCEL (.xlsx):
   *       - Crear una hoja por cada tabla seleccionada
   *       - Hoja "Resultados Asesores" (si incluir_asesores='true')
   *       - Hoja "Comisiones Supervisores" (si incluir_supervisores='true')
   *
   *    B) PARA PDF:
   *       - Crear secciones separadas por cada tabla
   *       - Incluir títulos claros para cada sección
   *
   * 4. ESTRUCTURA DE DATOS A INCLUIR:
   *
   *    TABLA RESULTADOS POR ASESOR (si incluir_asesores='true'):
   *    Columnas principales:
   *      - Asesor (asesor_nombre)
   *      - Total Planes (total_planes)
   *      - Margen (margen * 100 para porcentaje)
   *      - Total Vendido (total_vendido)
   *      - Bono (bono)
   *      - Alimentación (alimentacion)
   *      - Movilización (movilizacion)
   *      - Contratos Cancelados (chargeback_count)
   *      - Total CB (chargeback_total)
   *      - Total Comisión (total_comision)
   *
   *    TABLA COMISIONES DE SUPERVISORES (si incluir_supervisores='true'):
   *    Columnas principales:
   *      - Supervisor (nombre)
   *      - Contratos (total_contratos_global)
   *      - Base Neta (base_neta)
   *      - % Aplicado (porcentaje_aplicado)
   *      - Comisión Variable (comision_variable)
   *      - Beneficios (total_beneficios)
   *      - Total Comisión (total_comision)
   *
   * IMPORTANTE:
   * - Validar que al menos una tabla esté seleccionada
   * - Si ambos son 'false', retornar error 400
   * - El frontend ya envía correctamente los parámetros
   * - Solo falta que el backend los procese
   *
   * ============================================================================
   */
  const handleExportarConTablas = async (
    formato: 'xlsx' | 'pdf',
    tablas: { asesores: boolean; supervisores: boolean }
  ) => {
    if (!currentRunId) return;

    setIsExporting(true);
    setExportType(formato);
    setExportProgress(0);

    // Simular progreso
    const progressInterval = setInterval(() => {
      setExportProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 300);

    try {
      // Construir URL con parámetros de tablas
      const params = new URLSearchParams();
      params.append('formato', formato);
      if (tablas.asesores) params.append('incluir_asesores', 'true');
      if (tablas.supervisores) params.append('incluir_supervisores', 'true');

      const url = `${urlBackend}api/v1/comisiones/runs/${currentRunId}/export/?${params.toString()}`;

      // Importar erpApi para hacer la petición
      const { get } = erpApi();
      const resp = await get<any>(url, true, false);

      // Extraer el blob de la respuesta
      const blobData = resp?.data || resp;

      // Crear descarga del archivo
      const blob = new Blob([blobData], {
        type: formato === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `reporte_comisiones_${currentRunId}.${formato}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      setExportProgress(100);

      // Mantener el modal un momento más para mostrar 100%
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
        setExportType('');
      }, 500);
    } catch (error) {
      setIsExporting(false);
      setExportProgress(0);
      setExportType('');
    } finally {
      clearInterval(progressInterval);
    }
  };

  const handleExportarChargeback = async () => {
    if (!resultados || resultados.length === 0) return;

    setIsExporting(true);
    setExportType('xlsx');
    setExportProgress(0);

    try {
      setExportProgress(30);

      // Importar XLSX dinámicamente
      //const XLSX = await import('xlsx');

      // Preparar datos para el reporte
      const reporteData: any[] = [];

      resultados.forEach((asesor) => {
        if (asesor.contratos_cancelados && asesor.contratos_cancelados.length > 0) {
          // Agregar fila de encabezado del asesor
          reporteData.push({
            'Asesor': asesor.asesor_nombre,
            'Cliente': '',
            'Plan': '',
            'Valor Mensual': '',
            'Fecha Instalación': '',
            'Fecha Final': '',
            'Tiempo Transcurrido': '',
          });

          // Agregar contratos cancelados
          asesor.contratos_cancelados.forEach((contrato) => {
            reporteData.push({
              'Asesor': '',
              'Cliente': contrato.nombre_cliente,
              'Plan': contrato.plan,
              'Valor Mensual': `$${Number(contrato.valor_mensual).toFixed(2)}`,
              'Fecha Instalación': contrato.fecha_instalacion,
              'Fecha Final': contrato.fecha_final,
              'Tiempo Transcurrido': contrato.tiempo_transcurrido,
            });
          });

          // Agregar fila de total del asesor
          reporteData.push({
            'Asesor': '',
            'Cliente': '',
            'Plan': 'TOTAL DESCUENTO',
            'Valor Mensual': `-$${Number(asesor.chargeback_total).toFixed(2)}`,
            'Fecha Instalación': '',
            'Fecha Final': '',
            'Tiempo Transcurrido': `${asesor.chargeback_count} contratos`,
          });

          // Fila en blanco para separar
          reporteData.push({
            'Asesor': '',
            'Cliente': '',
            'Plan': '',
            'Valor Mensual': '',
            'Fecha Instalación': '',
            'Fecha Final': '',
            'Tiempo Transcurrido': '',
          });
        }
      });

      setExportProgress(70);

      // Crear workbook y worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(reporteData);

      // Ajustar ancho de columnas
      ws['!cols'] = [
        { wch: 30 }, // Asesor
        { wch: 35 }, // Cliente
        { wch: 25 }, // Plan
        { wch: 15 }, // Valor Mensual
        { wch: 18 }, // Fecha Instalación
        { wch: 18 }, // Fecha Final
        { wch: 20 }, // Tiempo Transcurrido
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Chargebacks');

      // Descargar archivo
      const fecha = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `Reporte_Chargebacks_${fecha}.xlsx`);

      setExportProgress(100);
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      // Error silencioso en producción
    } finally {
      setIsExporting(false);
      setExportType('');
      setExportProgress(0);
    }
  };

  /**
   * Handler para registrar chargebacks en el histórico
   */
  const handleRegistrarChargebacks = async () => {
    if (!currentRunId) {
      return;
    }

    try {
      await registrarCBMutation.mutateAsync(currentRunId);
      setOpenRegistrarCBDialog(false);
    } catch (error) {
      // Error silencioso en producción
    }
  };

  // Ordenar resultados por total_planes de mayor a menor
  const resultadosOrdenados = useMemo(() => {
    if (!resultados || resultados.length === 0) return [];
    return [...resultados].sort((a, b) => Number(b.total_planes) - Number(a.total_planes));
  }, [resultados]);

  return (
    <Box>
      {/* Indicador de Período */}
      <PeriodoIndicator onOpenParametrizacion={onOpenParametrizacion} tipoComision="Asesor" />

      {/* Modal de Calculo */}
      <Dialog
        open={isCalculating}
        maxWidth="sm"
        fullWidth
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setIsCalculating(false);
        }}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1, pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CalculateIcon sx={{ fontSize: 56, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Calculando Comisiones
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, gap: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Aplicando reglas de comisión y calculando totales
            </Typography>

            <Box sx={{ width: '100%' }}>
              <LinearProgress
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: 'primary.50',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    bgcolor: 'primary.main',
                  },
                }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              Este proceso puede tardar unos momentos...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Diálogo de Selección de Exportación */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileDownloadIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Descargar Reporte de Comisiones
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {/* Selección de Formato */}
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                Formato de Exportación
              </FormLabel>
              <RadioGroup
                value={selectedExportFormat}
                onChange={(e) => setSelectedExportFormat(e.target.value as 'xlsx' | 'pdf')}
              >
                <FormControlLabel
                  value="xlsx"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Description color="success" />
                      <Typography>Excel (.xlsx)</Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="pdf"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PictureAsPdf color="error" />
                      <Typography>PDF</Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Divider />

            {/* Selección de Tablas */}
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                Tablas a Incluir
              </FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTables.asesores}
                      onChange={(e) => setSelectedTables(prev => ({ ...prev, asesores: e.target.checked }))}
                    />
                  }
                  label="Resultados por Asesor"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTables.supervisores}
                      onChange={(e) => setSelectedTables(prev => ({ ...prev, supervisores: e.target.checked }))}
                      disabled={!coordinadorData || coordinadorData.length === 0}
                    />
                  }
                  label={
                    <Box>
                      <Typography>Comisiones de Supervisores</Typography>
                      {(!coordinadorData || coordinadorData.length === 0) && (
                        <Typography variant="caption" color="text.secondary">
                          (No hay datos disponibles)
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </FormGroup>
              {!selectedTables.asesores && !selectedTables.supervisores && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Debes seleccionar al menos una tabla para exportar
                </Alert>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setExportDialogOpen(false)}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained"
            disabled={!selectedTables.asesores && !selectedTables.supervisores}
            startIcon={<FileDownloadIcon />}
          >
            Descargar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Exportación */}
      <Dialog
        open={isExporting}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            minWidth: 400,
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {exportType === 'xlsx' ? (
              <Description sx={{ fontSize: 48, color: 'success.main' }} />
            ) : (
              <PictureAsPdf sx={{ fontSize: 48, color: 'error.main' }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Exportando {exportType === 'xlsx' ? 'Excel' : 'PDF'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                {exportProgress < 50 ? 'Preparando datos...' :
                 exportProgress < 80 ? 'Generando archivo...' :
                 'Finalizando descarga...'}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={exportProgress}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              {exportProgress}% completado
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
              Por favor espere, esto puede tomar unos segundos...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmación para Registrar Chargebacks */}
      <Dialog
        open={openRegistrarCBDialog}
        onClose={() => !registrarCBMutation.isPending && setOpenRegistrarCBDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600, fontSize: '1.25rem' }}>
          ⚠️ Confirmar Registro de Chargebacks
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ¿Está seguro de registrar los valores de CB mostrados en la pantalla anterior?
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Importante:
            </Typography>
            <Typography variant="body2">
              El próximo mes estos valores <strong>no serán tomados en cuenta</strong> para los cálculos,
              evitando así descuentos duplicados en períodos consecutivos.
            </Typography>
          </Alert>
          <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Período:</strong> {filtros.fecha_inicio} - {filtros.fecha_fin}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Run ID:</strong> {currentRunId}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenRegistrarCBDialog(false)}
            disabled={registrarCBMutation.isPending}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleRegistrarChargebacks}
            variant="contained"
            color="success"
            disabled={registrarCBMutation.isPending}
            startIcon={registrarCBMutation.isPending ? <CircularProgress size={20} /> : <SaveAlt />}
          >
            {registrarCBMutation.isPending ? 'Registrando...' : 'Confirmar Registro'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Filtros de Búsqueda
          </Typography>

          {/* Alert de error en carga de filtros */}
          {!isLoadingFiltros && canalesDisponibles.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                No se pudieron cargar los canales de venta. Por favor, intenta recargar usando el botón de actualización.
              </Typography>
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Date Range Picker - OCULTO: Se usa el período configurado en Períodos */}
            {/* <Grid item xs={12} sm={6} md={4} lg={3}>
              <DateRangePicker
                size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                flatpickrRef={flatpickrRef}
                onChange={handleChangeRangeDate}
                defaultValue={`${filtros.fecha_inicio} to ${filtros.fecha_fin}`}
                label="Rango de Fechas"
              />
            </Grid> */}

            {/* Canales de Venta */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="canales-venta-label">Canales de Venta</InputLabel>
                <Select
                  labelId="canales-venta-label"
                  multiple
                  value={selectedCanales}
                  onChange={handleChangeCanales}
                  input={<OutlinedInput label="Canales de Venta" />}
                  disabled={isLoadingFiltros || canalesDisponibles.length === 0}
                  renderValue={(selected) =>
                    selected.length === canalesDisponibles.length ? 'Todos' : selected.join(', ')
                  }
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 300 },
                    },
                  }}
                  onClose={() => setCanalSearch('')}
                >
                  <Box sx={{ px: 1.5, py: 0.75, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Buscar canal..."
                      fullWidth
                      value={canalSearch}
                      onChange={(e) => setCanalSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                  <MenuItem value="SELECCIONAR_TODOS" dense>
                    <Checkbox checked={selectedCanales.length === canalesDisponibles.length} size="small" />
                    <ListItemText primary="Seleccionar todos" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                  </MenuItem>
                  {canalesFiltrados.length > 0 ? (
                    canalesFiltrados.map((canal: string) => (
                      <MenuItem key={canal} value={canal} dense>
                        <Checkbox checked={selectedCanales.indexOf(canal) > -1} size="small" />
                        <ListItemText primary={canal} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled dense>
                      <ListItemText primary="No se encontraron canales" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Botón Recargar Canales */}
            <Grid item xs={12} sm={6} md={1} lg="auto">
              <Tooltip title="Recargar Canales">
                <IconButton
                  color="primary"
                  onClick={handleRefreshFiltros}
                  disabled={isLoadingFiltros}
                  sx={{
                    height: '40px',
                    width: '40px',
                    border: 1,
                    borderColor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.light',
                      borderColor: 'primary.dark'
                    },
                    '&.Mui-disabled': {
                      borderColor: 'action.disabled'
                    }
                  }}
                >
                  {isLoadingFiltros ? <CircularProgress size={20} /> : <Refresh fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Grid>

            {/* Divider */}
            <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
              <Divider orientation="vertical" flexItem sx={{ height: '40px', borderRightWidth: 2 }} />
            </Grid>

            {/* Botón Calcular Comisiones */}
            <Grid item xs={12} sm="auto" md="auto">
              <Button
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<CalculateIcon />}
                onClick={handleCalcular}
                disabled={calcularMutation.isPending || isLoadingFiltros}
                sx={{
                  minWidth: { xs: 'auto', sm: 140 },
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 2,
                  '&:hover': { boxShadow: 4 },
                  height: '40px'
                }}
              >
                Calcular
              </Button>
            </Grid>

            {/* Botón Limpiar Filtros */}
            <Grid item xs={12} sm="auto" md="auto">
              <Button
                variant="outlined"
                color="primary"
                size="medium"
                startIcon={<Refresh />}
                onClick={() => {
                  handleClearDates();
                  setFiltros((prev) => ({
                    ...prev,
                    canales: canalesDisponibles,
                  }));
                }}
                sx={{
                  minWidth: { xs: 'auto', sm: 140 },
                  textTransform: 'none',
                  fontWeight: 600,
                  height: '40px'
                }}
              >
                Limpiar Filtros
              </Button>
            </Grid>

            {/* Botón Registrar CB - Solo visible si hay resultados */}
            {currentRunId && (
              <Grid item xs={12} sm="auto" md="auto">
                <Button
                  variant="contained"
                  color="success"
                  size="medium"
                  startIcon={<SaveAlt />}
                  onClick={() => setOpenRegistrarCBDialog(true)}
                  disabled={registrarCBMutation.isPending}
                  sx={{
                    minWidth: { xs: 'auto', sm: 160 },
                    textTransform: 'none',
                    fontWeight: 600,
                    height: '40px',
                    boxShadow: 2,
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  Registrar CB
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
      {/* Banner de recalcular */}
      {currentRunId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            ✓ Datos compartidos - Período {filtros.fecha_inicio} - {filtros.fecha_fin}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Canales de venta:</strong> {canalesCalculados && canalesCalculados.length > 0 ? canalesCalculados.join(', ') : 'Todos'}
          </Typography>
          <Typography variant="body2">
            Estos resultados están sincronizados con los tabs de Líder y Jefatura.
            Si cambias los parámetros en la configuración, debes recalcular para aplicar los cambios.
          </Typography>
        </Alert>
      )}

      {/* Sección de Botones de Descarga - Solo si hay resultados */}
      {(resultados && resultados.length > 0) || (coordinadorData && coordinadorData.length > 0) ? (
        <Card elevation={2} sx={{ mb: 3, bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FileDownloadIcon /> Opciones de Descarga
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Exporta los reportes de comisiones en formato Excel o PDF
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleOpenExportDialog}
                  disabled={exportarMutation.isPending}
                  sx={{
                    minWidth: '180px',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    }
                  }}
                >
                  Descargar Reporte
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportarChargeback}
                  disabled={!resultados || resultados.length === 0}
                  sx={{
                    minWidth: '180px',
                    fontWeight: 600,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      bgcolor: 'warning.light',
                    }
                  }}
                >
                  Reporte Chargebacks
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : null}

      {/* Tabla de Supervisores */}
      {coordinadorData && coordinadorData.length > 0 && (
        <Card elevation={3} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <span>👤</span> Comisiones de Supervisores
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'warning.50' }}>
                    <TableCell sx={{ fontWeight: 700 }} />
                    <TableCell sx={{ fontWeight: 700 }}>Supervisor</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Contratos</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Base Neta</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>% Aplicado</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Comisión Variable</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Beneficios</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'success.100' }}>Total Comisión</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coordinadorData.map((supervisor, index) => (
                    <React.Fragment key={supervisor.id || index}>
                      <TableRow
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          bgcolor: expandedSupervisor === index ? 'warning.50' : 'inherit'
                        }}
                        onClick={() => setExpandedSupervisor(expandedSupervisor === index ? null : index)}
                      >
                        <TableCell align="center">
                          <IconButton size="small">
                            {expandedSupervisor === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{supervisor.nombre}</TableCell>
                        <TableCell align="right">{supervisor.total_contratos_global}</TableCell>
                        <TableCell align="right">${Number(supervisor.base_neta).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ color: 'info.main', fontWeight: 600 }}>
                          {Number(supervisor.porcentaje_aplicado).toFixed(2)}%
                        </TableCell>
                        <TableCell align="right">${Number(supervisor.comision_variable).toFixed(2)}</TableCell>
                        <TableCell align="right">${Number(supervisor.total_beneficios).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main', bgcolor: 'success.50' }}>
                          ${Number(supervisor.total_comision).toFixed(2)}
                        </TableCell>
                      </TableRow>

                      {/* Fila expandible con desglose */}
                      <TableRow>
                        <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                          <Collapse in={expandedSupervisor === index} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'warning.dark', mb: 2 }}>
                                📊 Desglose del Cálculo - {supervisor.nombre}
                              </Typography>

                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Base Neta */}
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.dark', mb: 1 }}>
                                    💰 Cálculo de Base Neta
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Total Vendido Grupo</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        ${Number(supervisor.total_vendido_grupo).toFixed(2)}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Ajuste Monetario (Chargebacks)</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                        -${Number(supervisor.ajuste_monetario_grupo).toFixed(2)}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ ml: 'auto', border: '2px solid', borderColor: 'primary.main', px: 2, py: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" color="text.secondary">Base Neta</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                        ${Number(supervisor.base_neta).toFixed(2)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Paper>

                                {/* Comisión Variable */}
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.dark', mb: 1 }}>
                                    📈 Comisión Variable
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Base Neta</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        ${Number(supervisor.base_neta).toFixed(2)}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Porcentaje</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.dark' }}>
                                        {Number(supervisor.porcentaje_aplicado).toFixed(2)}%
                                      </Typography>
                                    </Box>
                                    <Box sx={{ ml: 'auto', border: '2px solid', borderColor: 'info.main', px: 2, py: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" color="text.secondary">Comisión Variable</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'info.main' }}>
                                        ${Number(supervisor.comision_variable).toFixed(2)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Paper>

                                {/* Beneficios */}
                                {supervisor.total_beneficios > 0 && (
                                  <>
                                    {supervisor.bono_escalon > 0 && (
                                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark', mb: 1 }}>
                                          💎 Bonos por Escalón
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                          <Typography variant="body2" color="text.secondary">
                                            Bonos aplicados según umbral de contratos del rol Supervisor
                                          </Typography>
                                          <Box sx={{ bgcolor: 'success.main', px: 2, py: 1, borderRadius: 1, ml: 'auto' }}>
                                            <Typography variant="caption" sx={{ color: 'white' }}>Total Bonos</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'white' }}>
                                              ${Number(supervisor.bono_escalon).toFixed(2)}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Paper>
                                    )}

                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                      {supervisor.alimentacion > 0 && (
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.50', flex: 1, minWidth: 200 }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.dark', mb: 1 }}>
                                            🍽️ Vale de Alimentación
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Según umbral del rol Supervisor
                                          </Typography>
                                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                                            ${Number(supervisor.alimentacion).toFixed(2)}
                                          </Typography>
                                        </Paper>
                                      )}
                                      {supervisor.movilizacion > 0 && (
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'secondary.50', flex: 1, minWidth: 200 }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.dark', mb: 1 }}>
                                            🚗 Vale de Movilización
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Según umbral del rol Supervisor
                                          </Typography>
                                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'secondary.dark' }}>
                                            ${Number(supervisor.movilizacion).toFixed(2)}
                                          </Typography>
                                        </Paper>
                                      )}
                                    </Box>

                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.50' }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                                          Total Beneficios (Bonos + Vales)
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                                          ${Number(supervisor.total_beneficios).toFixed(2)}
                                        </Typography>
                                      </Box>
                                    </Paper>
                                  </>
                                )}

                                {/* Total */}
                                <Paper elevation={2} sx={{ p: 2, bgcolor: 'success.main' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                                        Total Comisión Supervisor
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>
                                        Comisión Variable + Beneficios
                                      </Typography>
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                                      ${Number(supervisor.total_comision).toFixed(2)}
                                    </Typography>
                                  </Box>
                                </Paper>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tabla de Resultados */}
      {resultados && resultados.length > 0 && (
        <Card elevation={2}>
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Resultados por Asesor
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {resultados.length} {resultados.length === 1 ? 'registro encontrado' : 'registros encontrados'}
              </Typography>
            </Box>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'auto',
                maxHeight: '600px'
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600 }} />
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Asesor</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Total Planes</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Margen</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Total Vendido</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Bono</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Alimentación</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Movilización</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Contratos Cancelados</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Total CB</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Total Comisión</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultadosOrdenados.map((row) => (
                    <Row key={row.id} row={row} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Estado de carga */}
      {isLoadingResultados && (
        <Card elevation={1}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                gap: 2
              }}
            >
              <CircularProgress size={48} />
              <Typography variant="h6" color="text.secondary">
                Cargando resultados...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Por favor espere un momento
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Sin resultados */}
      {!currentRunId && !isLoadingResultados && (
        <Card elevation={1}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                gap: 2
              }}
            >
              <CalculateIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
              <Typography variant="h6" color="text.secondary">
                No hay resultados para mostrar para Asesor
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
                Selecciona canales y luego haz clic en "Calcular" para generar el reporte.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
